import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";

export default function VerifyEmailPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | ok | fail
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        await refresh();
        setState("ok");
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
        setState("fail");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-6" data-testid="verify-email-page">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <div className="mx-auto flex items-center justify-center">
          <RecrystaMark size={48} />
        </div>
        {state === "verifying" && (
          <>
            <h1 className="mt-4 text-xl font-bold text-slate-900">Verifying your email…</h1>
            <p className="mt-2 text-sm text-slate-500">One moment.</p>
          </>
        )}
        {state === "ok" && (
          <>
            <div className="mt-4 mx-auto h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <h1 className="mt-4 text-xl font-bold text-slate-900" data-testid="verify-success">Email verified</h1>
            <p className="mt-2 text-sm text-slate-500">You&apos;ll receive your Monday briefing at this address.</p>
            <button
              onClick={() => nav("/dashboard/forecast")}
              className="mt-6 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800"
              data-testid="verify-go-to-dashboard"
            >
              Go to dashboard →
            </button>
          </>
        )}
        {state === "fail" && (
          <>
            <div className="mt-4 mx-auto h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <h1 className="mt-4 text-xl font-bold text-slate-900">Link expired or invalid</h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="verify-error">{err}</p>
            <Link
              to="/dashboard/settings"
              className="mt-6 inline-block rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              Request a new link from Settings
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
